import React, { useEffect, useMemo, useState } from "react";

/** Common Components */
import CardSection from "../../../components/ui/card/CardSection";

/** Lazily Loaded Components */
import DevelopmentFilters from "./Development.filters";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import CustomCenterModel from "../../../components/ui/customCenterModel/CustomCenterModel";
import { AppIcons } from "../../../data/appIcons";
import AddEditTodoItem from "./AddEditTodoItem";
import DevelopmentDetails from "./DevelopmentDetails";
import { useDevelopmentListUpdateSortOrderMutation, useDevelopmentToDoUpdatedStatusMutation, useGetDevelopmentTodoListMutation } from "../../../app/services/developmetListAPI";
import TodoDocument from "./TodoDocument";
import ToastService from "../../../services/toastService/ToastService";
import FormInputFields from "../../../components/finalForms/formFields/FormInputFields";
import Buttons from "../../../components/ui/button/Buttons";
import { DevelopmentStatus } from "../../../utils/ENUMS/developmentEnum";

const ManageDevelopment = () => {
    const [todoModal, setTodoModal] = useState(false);
    const [isDetailsModelOpen, setIsDetailsModelOpen] = useState(false);
    const [todoData, setTodoData] = useState();
    const [documentModelOpen, setDocumentModelOpen] = useState(false)

    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedPriority, setSelectedPriority] = useState([]);

    const [selectedRow, setSelectedRow] = useState(null);
    const [isNewSortModelOpen, setIsNewSortModelOpen] = useState(false);
    const [newSortOrder, setNewSortOrder] = useState("");

    const [filters, setFilters] = useState({
        pendingStatus: true,
        inProgressStatus: true,
        awaitingClarificationStatus: true,
        readyForReviewStatus: true,
        completedStatus: true,
    });

    const [gridData, setGridData] = useState([]);

    const [getTodoList, {
        isSuccess: getTodoListFetched,
        data: getTodoListData
    }] = useGetDevelopmentTodoListMutation();

    const [developmentListUpdateSortOrder, {
        isSuccess: developmentListUpdateSortOrderFetched,
        data: developmentListUpdateSortOrderData
    }] = useDevelopmentListUpdateSortOrderMutation();


    const [developmentToDoUpdatedStatus, {
        isSuccess: developmentToDoUpdatedStatusFetched,
        data: developmentToDoUpdatedStatusData
    }] = useDevelopmentToDoUpdatedStatusMutation();

    const formattedStatus = selectedStatus?.map(x => x.value).join(",") || "";
    const formattedPriority = selectedPriority?.value === "0" ? "" : selectedPriority?.value?.toString() || "";
    const request = {
        plToDoID: 0,
        psToDoStatusIDs: formattedStatus,
        prmIncludeDetails: false,
        plToDoPriorityKey: formattedPriority
    };

    useEffect(() => {
        const formattedStatus = selectedStatus?.map(x => x.value).join(",") || "";
        const formattedPriority = selectedPriority?.value === "0" ? "" : selectedPriority?.value?.toString() || "";
        const request = {
            plToDoID: 0,
            psToDoStatusIDs: formattedStatus,
            prmIncludeDetails: false,
            plToDoPriorityKey: formattedPriority
        };
        getTodoList(request);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, selectedPriority]);

    const getList = () => {
        getTodoList(request);
    }

    useEffect(() => {
        if (getTodoListFetched && getTodoListData) {
            const updated = getTodoListData.map((item, index) => ({
                ...item,
                displayOrder: index + 1
            }));
            setTodoData(updated);
            setGridData(updated);
        }
    }, [getTodoListFetched, getTodoListData]);


    useEffect(() => {
        if (developmentListUpdateSortOrderFetched && developmentListUpdateSortOrderData) {
            ToastService.success(developmentListUpdateSortOrderData?.errorMessage);
            getList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [developmentListUpdateSortOrderFetched, developmentListUpdateSortOrderData])

    useEffect(() => {
        if (developmentToDoUpdatedStatusFetched && developmentToDoUpdatedStatusData) {
            ToastService.success(developmentToDoUpdatedStatusData?.errorMessage);
            getList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [developmentToDoUpdatedStatusFetched, developmentToDoUpdatedStatusData])

    const handleFilterChange = (updatedFilters) => {
        setFilters({
            ...filters,
            ...updatedFilters,
        });
    };

    const handleCloseModel = () => {
        setTodoModal(false)
        setIsDetailsModelOpen(false)
        setDocumentModelOpen(false)
        setIsNewSortModelOpen(false)
    }

    const handleAddClick = () => {
        setTodoModal(true);
        setSelectedRow(null);
    };

    const handalRowEditClick = (rowData) => {
        setSelectedRow(rowData);
        setTodoModal(true);
    }

    const handleOpenMoveToModal = (rowData) => {
        setSelectedRow(rowData);
        setIsNewSortModelOpen(true);
    };

    const displayedGridData = useMemo(() => {
        return [...gridData].sort((a, b) => a.toDoSortOrder - b.toDoSortOrder);
    }, [gridData]);

    const handleNewSortOrder = (dataField, value) => {
        setNewSortOrder(value);
    }

    const handleMoveTask = (rowData, direction) => {

        const currentIndex = displayedGridData.findIndex(
            item => item.toDoID === rowData.toDoID
        );
        if (currentIndex === -1) return;

        const currentSortOrder = displayedGridData[currentIndex].toDoSortOrder;

        let targetIndex = null;

        if (direction === "Up" || direction === "Down") {

            targetIndex = direction === "Up" ? currentIndex - 1 : currentIndex + 1;

            if (targetIndex < 0 || targetIndex >= displayedGridData.length) {
                return;
            }
        }
        if (direction === "To") {
            const newIndex = parseInt(newSortOrder);

            if (!newIndex || newIndex < 1) {
                return;
            }

            targetIndex = newIndex - 1;

            if (targetIndex < 0 || targetIndex >= displayedGridData.length) {
                return;
            }
        }

        const newSortOrderIndex = displayedGridData[targetIndex].toDoSortOrder;
        const request = {
            plToDoID: rowData.toDoID,
            plNewSortOrder: newSortOrderIndex,
            plSortOrder: currentSortOrder,
            psDirection: direction
        }

        developmentListUpdateSortOrder(request);

        const updated = [...displayedGridData];
        const movedRow = updated.splice(currentIndex, 1)[0];
        updated.splice(targetIndex, 0, movedRow);
        const updatedGrid = updated.map((x, idx) => ({
            ...x,
            displayOrder: idx + 1
        }));

        setGridData(updatedGrid);
        setIsNewSortModelOpen(false);
        setNewSortOrder("");
    };

    const handleTodoUpdatedStatus = (row, statusID) => {
        const request = {
            plToDoID: row.toDoID,
            plStatusID: statusID
        }
        developmentToDoUpdatedStatus(request)
    }

    const rowClass = (rowData) => {
        return {
            'blue-color-row':
                rowData.toDoStatusID === DevelopmentStatus.PENDING ||
                rowData.toDoStatusID === DevelopmentStatus.INPROGRESS,
            'red-color-row': rowData.toDoStatusID === DevelopmentStatus.AWAITINGCLARIFICATION,
            'green-color-row': rowData.toDoStatusID === DevelopmentStatus.READYFORREVIEW
        };
    };

    const getCtxMenuItemsCovered = (row) => {
        if (!row) return [];
        return [
            {
                label: "New To Do Item",
                command: () => {
                    handleAddClick(row)
                }
            },
            {
                label: "Edit To Do Item",
                command: () => {
                    handalRowEditClick(row);
                }
            },
            {
                label: "Details",
                command: () => {
                    setIsDetailsModelOpen(true);
                    setTodoData(row);
                }
            },
            {
                label: "Mark Awaiting Clarification",
                command: () => {
                    handleTodoUpdatedStatus(row, DevelopmentStatus.AWAITINGCLARIFICATION)
                }
            },
            {
                label: "Mark Ready for Review",
                command: () => {
                    handleTodoUpdatedStatus(row, DevelopmentStatus.READYFORREVIEW)
                }
            },
            {
                label: "Move Up",
                command: () => {
                    handleMoveTask(row, 'Up');
                }
            },
            {
                label: "Move Down",
                command: () => {
                    handleMoveTask(row, 'Down')
                }
            },
            {
                label: "Move To",
                command: () => {
                    handleOpenMoveToModal(row);
                }
            },
            {
                label: "Documents",
                command: () => {
                    setDocumentModelOpen(true);
                    setTodoData(row);
                }
            }
        ];
    };

    return (
        <>
            <div className="scrollable-table-sec head-center">
                <div className="main-filters">
                    <DevelopmentFilters
                        filter={filters}
                        onFilterChange={handleFilterChange}
                        onStatusChange={(status) => setSelectedStatus(status)}
                        onPriorityChange={(priority) => setSelectedPriority(priority)}
                    />
                </div>
                <CardSection
                    cardTitle={`Development List`}
                    isButtonVisible={true}
                    titleButtonClick={handleAddClick}
                    rightButton={true}
                    buttonClassName="blue-btn"
                    buttonText="Add"
                >
                    <MolGrid
                        value={gridData}
                        expandableRowGroups={true}
                        paginator={false}
                        scrollable
                        scrollHeight="flex"
                        filterDisplay="row"
                        sortMode="single"
                        showGridlines
                        resizableColumns
                        ctxMenuItems={getCtxMenuItemsCovered}
                        rowClassName={rowClass}
                    >
                        {/* <Column field="toDoSortOrder" header="Order" grouping sortable /> */}
                        <Column field="displayOrder" header="Order" grouping sortable />
                        <Column field="toDoStatus" header="Status" grouping sortable />
                        <Column field="sToDoPriority" header="Priority" grouping sortable />
                        <Column field="toDoRequestDate" header="Request Date" grouping sortable />
                        <Column field="requestedDeliveryTimeframe" header="Requested Timeframe" grouping sortable />
                        <Column field="toDoCompletionDate"  colType="datetime"  format="mm/dd/yyyy" header="Completion Date" grouping sortable />
                        <Column field="toDoTitle" header="To Do Item" grouping sortable />
                        <Column field="sUserFullName" header="Requestor" grouping sortable />
                    </MolGrid>
                </CardSection>
            </div>
            <CustomCenterModel
                isOpen={todoModal}
                useCustomBg={true}
                modalTitle={selectedRow ? "Edit To Do List Item" : "To Do List Item"}
                contentClass='content-45'
                onClose={handleCloseModel}
                modalTitleIcon={AppIcons.AddIcon}
            >
                <AddEditTodoItem
                    isOpen={todoModal}
                    rowData={selectedRow}
                    onClose={handleCloseModel}
                    getList={getList}
                />
            </CustomCenterModel>
            <CustomCenterModel
                isOpen={isDetailsModelOpen}
                useCustomBg={true}
                modalTitle={"To Do Item Details"}
                contentClass="content-40"
                onClose={() => setIsDetailsModelOpen(false)}
                modalTitleIcon={AppIcons.editIcon}
            >
                <DevelopmentDetails onClose={handleCloseModel} todoData={todoData} />
            </CustomCenterModel>
            <CustomCenterModel
                isOpen={documentModelOpen}
                useCustomBg={true}
                modalTitle={"To Do Documents"}
                contentClass="content-40"
                onClose={() => setDocumentModelOpen(false)}
                modalTitleIcon={AppIcons.editIcon}
            >
                <TodoDocument onClose={handleCloseModel} todoData={todoData} />
            </CustomCenterModel>
            <CustomCenterModel
                isOpen={isNewSortModelOpen}
                useCustomBg={true}
                modalTitle={"New Sort Order"}
                contentClass="content-30"
                onClose={() => setIsNewSortModelOpen(false)}
                modalTitleIcon={AppIcons.editIcon}
            >
                <>
                    <FormInputFields
                        dataField="newSortOrder"
                        value={newSortOrder}
                        labelName="New Sort Order :"
                        isRequired={true}
                        type="text"
                        name=""
                        onChange={handleNewSortOrder}
                        error={newSortOrder === '' ? 'New sort order is required' : ''}

                    />
                    <div className="d-flex justify-content-end gap-3 mt-3">
                        <Buttons
                            buttonText="Save"
                            buttonTypeClassName="btn blue-btn"
                            onClick={() => { handleMoveTask(selectedRow, 'To') }}
                        />

                        <Buttons
                            buttonText="Cancel"
                            buttonTypeClassName="btn dark-btn"
                            onClick={handleCloseModel}
                        />
                    </div>
                </>
            </CustomCenterModel>
        </>
    );
};

export default ManageDevelopment;
